import { getRecoil } from "recoil-nexus";

import { ISwarmCreateBetCashierRequest } from "src/common/swarm/models/request";
import { BetTypeEnum } from "src/constants/enums/betType";
import { IPlaceBetRequestArg } from "src/constants/interfaces/bet";
import mainStore, { storeSetShowSuccessState, storeSetSuperbet } from "src/stores/main";
import upperDataStore, { storeGetConfig } from "src/stores/upperData";
import { ISwarmCreateBetCashierResponse } from "src/common/swarm/models/response";
import { emitBetPlaced } from "../communicator/actions";
import { storeChangeSelection } from "src/stores/selections";
import { toastWrapper } from "src/common/components/toastWrapper";
import { viewMessage } from "src/common/helpers/view";
import { InputMethodEnum } from "src/constants/enums/inputMethod";
import CashierSwarmActions from "../swarmActions/cashier";
import { getPrice } from "src/rules/calculation/priceCalculation";
import { PriceChangeEnum } from "src/constants/enums/priceChange";

class CashierPlaceBet {
  private static createBetRequest = ({ selections, amount }: IPlaceBetRequestArg) => {
    const {
      betType,
      isSuperbet,
      priceChangeType,
      systemCombinationCount,
      editingBet,
    } = getRecoil(mainStore);

    const { userId, memberId, verificationResponse } = getRecoil(upperDataStore);

    const isCounterOffer = selections.some(item => item.priceChangeType === PriceChangeEnum.CounterOffer);

    const request: ISwarmCreateBetCashierRequest = {
      AcceptTypeId: isCounterOffer ? PriceChangeEnum.CounterOffer : priceChangeType,
      TypeId: betType,
      Amount: amount,
      InputMethod: InputMethodEnum.CallOver,
      IsPlayerBetting: !!userId,
      IsPlayerAccountCharged: !!userId,
      IsSuperBet: isSuperbet,
      MemberId: verificationResponse?.data?.id || memberId,

      Source: 96,

      SelectionList: selections.map(item => ({
        SelectionId: item.event.id,
        Price: getPrice(item),
        IsBanker: betType === BetTypeEnum.System ? item.isBanker : null,
      })),
    };

    if (memberId || verificationResponse?.data?.id)
      request.MemberId = memberId || verificationResponse?.data?.id;

    if (editingBet)
      request.OldBetId = editingBet.betId;

    if (betType === BetTypeEnum.System && systemCombinationCount)
      request.SystemMinCount = systemCombinationCount;

    if (userId) {
      request.ClientId = userId;
    }

    return request;
  }

  private static makeBetRequest = (data: IPlaceBetRequestArg) => {
    const request = CashierPlaceBet.createBetRequest(data);
    return CashierSwarmActions.placeBet(request);
  };

  private static processRequestSingleList = ({ selections, amount }: IPlaceBetRequestArg) => {
    const config = storeGetConfig();

    const requests = selections.map(bet => {
      const stake = config.enableSingleStakeInput ? (bet.singleStake || 0) : amount;
      return CashierPlaceBet.makeBetRequest({ selections: [bet], amount: +stake });
    });

    return requests;
  };

  private static processRequest = (data: IPlaceBetRequestArg) => {
    const config = storeGetConfig();
    const { betType } = getRecoil(mainStore);

    if (config.enableSingles && betType === BetTypeEnum.Single) return CashierPlaceBet.processRequestSingleList(data);

    return [CashierPlaceBet.makeBetRequest(data)];
  };

  private static processResponse = (
    succeedBets: ISwarmCreateBetCashierResponse[],
    rejectedBets: ISwarmCreateBetCashierResponse[],
    timeout?: boolean,
  ) => {
    const mainState = getRecoil(mainStore);
    const config = storeGetConfig();

    if (timeout) {
      toastWrapper('error', 'Place bet timeout, please retry!');
      return [];
    }

    const noSuperbets = succeedBets.every(item => !item.IsSuperBet);

    !rejectedBets.length && noSuperbets && config.enableSuccessState && storeSetShowSuccessState(true);

    if (noSuperbets && mainState.isSuperbet) storeSetSuperbet(false);

    const succeedSelectionIds = succeedBets.reduce<number[]>((acc, item) => (
      [...acc, ...item.SelectionList.map(({ SelectionId }) => SelectionId)]
    ), []);

    return succeedSelectionIds;
  }

  public static placeBet = async (data: IPlaceBetRequestArg) => new Promise(async (resolve) => {
    const { selections } = data;
    const upperDataSatate = getRecoil(upperDataStore);

    const placeBetTimeout = setTimeout(() => {
      resolve(CashierPlaceBet.processResponse([], [], true))
    }, storeGetConfig().placeBetTimeoutSeconds * 1000);

    const responses = await Promise.allSettled(CashierPlaceBet.processRequest(data));

    const succeedBets = (
      responses.filter(({ status }) => status === 'fulfilled') as PromiseFulfilledResult<ISwarmCreateBetCashierResponse>[]
    ).map(({ value }) => value);

    // const config = storeGetConfig();
    // if (config.wheelConfig) {
    //   const lastBetId = succeedBets.at(-1)?.Id;
    //   if (typeof lastBetId === "number") storeTriggerdBetId(lastBetId);
    // }

    const rejectedBets = (
      responses.filter(({ status }) => status === 'rejected') as PromiseRejectedResult[]
    ).map(({ reason }) => reason);

    succeedBets.forEach((bet) => emitBetPlaced({
      data: bet,
      verification: upperDataSatate.verificationResponse,
    }));

    rejectedBets.forEach((details) => {
      // TODO: proper implementation of errors message
      const selectionId = +details.ErrorData?.SelectionId
      const selectionWithError = selections.find(item => item.event.id === selectionId)

      if (selectionWithError)
        storeChangeSelection(selectionId, { ...selectionWithError, hasError: true })

      toastWrapper('error', details.data.Message)
      viewMessage.error(details.data.Message, '');
      console.log(details.data.Message, 'placeBet: got error');
    });

    clearTimeout(placeBetTimeout);

    resolve(CashierPlaceBet.processResponse(succeedBets, rejectedBets));
  });
};

export default CashierPlaceBet;
